import { useState, useRef, useMemo, useEffect } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';

function SparkleIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Chip-list field with inline expandable add pill (PRD.214).        */
/*  "+ Add" pill opens an inline input + suggestion popover.          */
/* ------------------------------------------------------------------ */

export interface Chip {
  id: string;
  label: string;
  /** Optional small pill rendered inline after the label (e.g. "AI Suggested"). */
  badge?: string;
}

export interface ChipListFieldProps {
  chips: Chip[];
  suggestions: string[];
  onSearchChange?: (text: string) => void;
  onAdd: (label: string) => void;
  onRemove: (chip: Chip) => void;
  /** Helper subtitle shown above the chip row. */
  subtitle?: string;
  /** Label for the "+ Add …" pill. */
  addLabel?: string;
  /** Placeholder for the inline input when expanded. */
  placeholder?: string;
}

export function ChipListField({
  chips,
  suggestions,
  onSearchChange,
  onAdd,
  onRemove,
  subtitle,
  addLabel = 'Add',
  placeholder,
}: ChipListFieldProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const primarySoft = brandTokens['--color-primary-soft'] || 'rgba(37, 99, 235, 0.12)';

  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!editing) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setEditing(false);
        setShowSuggestions(false);
        setInputValue('');
        onSearchChange?.('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editing, onSearchChange]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const existingLabels = useMemo(
    () => new Set(chips.map((c) => c.label.trim().toLowerCase())),
    [chips],
  );
  const filteredSuggestions = useMemo(
    () => suggestions.filter((s) => !existingLabels.has(s.trim().toLowerCase())),
    [suggestions, existingLabels],
  );

  function commit(label: string) {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (!existingLabels.has(trimmed.toLowerCase())) {
      onAdd(trimmed);
    }
    setInputValue('');
    onSearchChange?.('');
    setShowSuggestions(false);
    // Stay in edit mode so the user can rapidly add multiple chips
    inputRef.current?.focus();
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setInputValue(v);
    onSearchChange?.(v);
    setShowSuggestions(v.trim().length > 0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(inputValue);
    } else if (e.key === 'Escape') {
      setEditing(false);
      setShowSuggestions(false);
      setInputValue('');
      onSearchChange?.('');
    } else if (e.key === 'Backspace' && inputValue === '' && chips.length > 0) {
      onRemove(chips[chips.length - 1]);
    }
  }

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {subtitle && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {subtitle}
        </div>
      )}

      {/* Chip row + inline expandable input/pill */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
        }}
      >
        {chips.map((chip) => (
          <span
            key={chip.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 4px 4px 12px',
              borderRadius: 'var(--radius-full)',
              background: primarySoft,
              color: primary,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 1.3,
            }}
          >
            {chip.label}
            {chip.badge && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  marginLeft: 4,
                  padding: '1px 7px',
                  borderRadius: 'var(--radius-full)',
                  background: primary,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  letterSpacing: 0.1,
                }}
              >
                <SparkleIcon />
                {chip.badge}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemove(chip)}
              title="Remove"
              style={{
                width: 18,
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                color: primary,
                opacity: 0.7,
                cursor: 'pointer',
                borderRadius: 'var(--radius-full)',
                padding: 0,
              }}
            >
              <NavIcon name="x" />
            </button>
          </span>
        ))}

        {/* Inline editor: shows when "+ Add" is clicked */}
        {editing ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 4px 2px 10px',
              borderRadius: 'var(--radius-full)',
              border: `1px solid ${primary}`,
              background: 'var(--bg-surface)',
              minWidth: 180,
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              placeholder={placeholder ?? `Type a ${addLabel.toLowerCase()}…`}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              style={{
                flex: 1,
                minWidth: 120,
                height: 24,
                padding: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text)',
              }}
            />
            <button
              type="button"
              onClick={() => commit(inputValue)}
              disabled={!inputValue.trim()}
              title="Add"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: 'none',
                background: inputValue.trim() ? primary : 'var(--text-disabled, #cbd5e1)',
                color: '#fff',
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <NavIcon name="check" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 12px 4px 8px',
              borderRadius: 'var(--radius-full)',
              border: `1px dashed ${primary}`,
              background: 'transparent',
              color: primary,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            <NavIcon name="plus" />
            {addLabel}
          </button>
        )}

        {/* Suggestions popover */}
        {editing && showSuggestions && filteredSuggestions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              minWidth: 220,
              maxWidth: 360,
              maxHeight: 220,
              overflowY: 'auto',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.08))',
              zIndex: 10,
            }}
          >
            {filteredSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(s)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
